import { HelpCenter } from "@sp-crm/core";
import { WhiteLabelContentMode } from "generated/graphql";
import React, { useEffect, useRef } from "react";
import { useWhiteLabelContentMode } from "store/selectors/branding";
import { useHelpCenter } from "store/selectors/hooks";
import { reportError, toReportableError } from "util/error-tracking";
import { beaconApi, beaconDestroyed, beaconReady } from "util/user-events";

const initializeBeacon = (beaconId: string) => {
    const Beacon = beaconApi();
    if (!Beacon) {
        return;
    }
    Beacon("init", beaconId);
    Beacon("config", { display: { style: "manual", position: "left" } });
    Beacon("once", "ready", beaconReady);
};

const destroyBeacon = () => {
    const Beacon = beaconApi();
    if (!Beacon) {
        return;
    }
    Beacon("destroy");
    beaconDestroyed();
};

const transitionBeacon = (
    mountedBeaconId: string | null,
    nextBeaconId: string | null,
) => {
    try {
        if (mountedBeaconId) {
            destroyBeacon();
        }
        if (nextBeaconId) {
            initializeBeacon(nextBeaconId);
        }
    } catch (error) {
        reportError(toReportableError(error));
    }
};

const desiredBeaconId = (
    contentMode: WhiteLabelContentMode,
    helpCenter: HelpCenter | null,
    mountedBeaconId: string | null,
): string | null => {
    if (contentMode !== WhiteLabelContentMode.Full) {
        return null;
    }
    return helpCenter ? helpCenter.beaconId : mountedBeaconId;
};

export const HelpCenterBeacon: React.FC = () => {
    const contentMode = useWhiteLabelContentMode();
    const helpCenter = useHelpCenter();
    const mountedBeaconId = useRef<string | null>(null);

    useEffect(() => {
        const nextBeaconId = desiredBeaconId(
            contentMode,
            helpCenter,
            mountedBeaconId.current,
        );
        if (!beaconApi() || nextBeaconId === mountedBeaconId.current) {
            return;
        }
        transitionBeacon(mountedBeaconId.current, nextBeaconId);
        mountedBeaconId.current = nextBeaconId;
    }, [contentMode, helpCenter]);

    return null;
};
